/**
 * Feeding the protocol's own facts to the step that writes a form's variables.
 *
 * The protocol reading stores facts (protocolAnalysis/latest: coding conventions,
 * demographic items, seriousness criteria, scales, reporting timelines) that the variable
 * writer never saw, so it wrote from general knowledge and a post-pass corrected some
 * of it afterwards. This module selects the facts relevant to ONE form and formats them as
 * a short, bounded block for the prompt. Pure: no model call, no database.
 *
 * Questionnaires: when the protocol text holds a section or appendix that defines the
 * instrument's items, that passage is included verbatim (bounded), found by the form
 * name's distinctive words.
 */
import { demographicNotCollected, factsOf, type Sourced } from "./crfProtocolReading";
import { IDENTIFIER_FEED_RULE, demographicsFactsForFeed } from "./directIdentifiers";
import { raceEthnicityExpected } from "./raceEthnicityEvidence";

/**
 * Race and ethnicity the protocol TEXT collects but the reading did not store (backlog 21 (bbb)), as facts for
 * the Demographics writer: the protocol's own category labels when it lists them, else the reason (a collection
 * sentence, NIH funding). Nothing when the reading already names the item or the protocol says it is not collected.
 */
function raceEthnicityFromText(analysis: Record<string, unknown>, protocolText: string | undefined): Sourced[] {
  if (!protocolText) return [];
  const listed = factsOf(analysis, "demographicsFields").map((f) => f.text).join(" | ");
  const out: Sourced[] = [];
  for (const [topic, word, label] of [["race", /\brace\b|racial/i, "Race"], ["ethnicity", /ethnic|hispanic|latino/i, "Ethnicity"]] as const) {
    if (word.test(listed) || demographicNotCollected(analysis, topic, protocolText)) continue;
    const ev = raceEthnicityExpected(protocolText, topic);
    if (!ev) continue;
    out.push({ text: ev.categories.length ? `${label} (the protocol lists: ${ev.categories.join("; ")})` : `${label} (collected: ${ev.why})`, section: "protocol text" });
  }
  return out;
}

export const FACT_BLOCK_HEADING = "Facts the protocol states (use these exactly; they override general conventions and CDASH defaults)";
export const DEFAULT_FACT_BLOCK_CHARS = 6000;
const PER_FACT_CHARS = 1500;
const PASSAGE_CHARS = 3500;

export interface FactFeedInput {
  analysis: Record<string, unknown> | null | undefined;
  formName: string;
  formCategory?: string;
  formCode?: string;
  /** The whole protocol text, when the caller has it (the questionnaire passage is read from it). */
  protocolText?: string;
  maxChars?: number;
}
export interface FactFeedResult {
  block: string;
  /** Facts or passages left out to stay within the limit. */
  cut: number;
}

type FormKind = "demographics" | "sae" | "ae" | "randomization" | "disposition" | "medhistory" | "conmed" | "questionnaire" | "other";

function kindsOf(name: string, category: string): Set<FormKind> {
  const k = new Set<FormKind>();
  const n = `${name} ${category}`;
  if (/demograph/i.test(n)) k.add("demographics");
  if (/serious adverse|\bSAE\b|serious event/i.test(n)) { k.add("sae"); k.add("ae"); }
  else if (/adverse|\bAEs?\b|\bADE\b|safety event/i.test(n)) k.add("ae");
  if (/randomi[sz]/i.test(n)) k.add("randomization");
  if (/disposition|end of study|study completion|discontinu|withdraw/i.test(n)) k.add("disposition");
  if (/medical history|history/i.test(n)) k.add("medhistory");
  if (/concomitant|\bconmeds?\b|prior (?:and|or) concomitant|medications?/i.test(n)) k.add("conmed");
  if (/questionnaire|\bscale\b|inventory|survey|\bindex\b|\bscore\b|diary|rating|instrument|\bPRO\b/i.test(n)) k.add("questionnaire");
  return k;
}

const STOP = new Set(["the", "and", "for", "with", "form", "forms", "log", "crf", "page", "short", "long", "version"]);
const GENERIC_INSTRUMENT = new Set(["questionnaire", "scale", "score", "scores", "index", "inventory", "survey", "assessment", "assessments", "instrument", "rating", "diary", "patient", "participant", "subject", "reported", "outcome", "outcomes", "pro"]);

const tokens = (s: string): string[] => s.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length >= 2 && !STOP.has(t));
const clip = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, n).trimEnd()}...`);

function describeFact(f: Sourced): string {
  const where = f.section ? ` (protocol section ${f.section.replace(/^(section|§)\s*/i, "")})` : "";
  let text = clip(f.text, PER_FACT_CHARS);
  if (f.codes && f.codes.length >= 2) {
    const pairs = f.codes.map((c) => `${c.code} = ${c.label}`);
    // Do not print the codes twice when the fact text already carries them.
    if (!pairs.every((p) => text.includes(p))) text += ` [codes: ${pairs.join("; ")}]`;
  }
  return `- ${text}${where}`;
}

/** True only for the STUDY-WIDE yes/no rule ("Yes/No questions", "all yes/no items", "Y/N"); a field-specific list that happens to use yes/no codes is not. */
export const isYesNoConvention = (f: Sourced): boolean => {
  // A parenthetical list of examples does not make the rule field-specific.
  const head = (f.field ?? f.text.split(/[:=]/)[0]).toLowerCase().replace(/\([^)]*\)/g, " ");
  if (!/\b(?:yes\s*\/\s*no|y\s*\/\s*n)\b/.test(head)) return false;
  const rest = head.replace(/\b(?:yes\s*\/\s*no|y\s*\/\s*n)\b/g, " ").split(/[^a-z0-9]+/).filter((t) => t && !YN_GENERIC.has(t));
  return rest.length === 0;
};
/** Words too common on forms to show that a convention is about THIS form. */
const GENERIC_FIELD_WORD = new Set(["criteria", "criterion", "assessment", "assessments", "form", "forms", "record", "records", "data", "details", "information", "status", "page", "event", "events"]);
const YN_GENERIC = new Set(["all", "any", "every", "questions", "question", "items", "item", "fields", "field", "answers", "answer", "responses", "response", "in", "this", "the", "study", "coded", "coding", "are", "is", "variables", "variable", "of", "type", "all"]);

/**
 * Words that say WHO or WHAT KIND of thing a form is about but not WHICH: two forms or a form and a
 * convention sharing only one of these are not about the same thing ("Child Assent Status" is not about
 * "Child Verbal Ability Assessment"; "behavior" does not make the EDI the Vineland). Backlog 21 (ww).
 */
const GENERIC_SUBJECT_WORD = new Set([
  "child", "children", "childs", "parent", "parents", "caregiver", "participant", "participants", "patient", "patients", "subject", "subjects",
  "adult", "adults", "teen", "teens", "youth", "study", "visit", "visits", "behavior", "behaviour", "behavioral", "behavioural", "behaviors", "behaviours",
  "problem", "problems", "situation", "situations", "usable", "usability", "system", "item", "items", "total", "rating", "ratings", "category",
  "categories", "result", "results", "type", "types", "level", "levels", "baseline", "follow", "test",
]);
/** Words that make a label the NAME of an instrument ("... Inventory", "... Scale", "Mayo score: ..."). */
const INSTRUMENT_NAME_WORD = /\b(?:questionnaire|scale|scales|inventory|index|measure|checklist|diary|chart|score|subscore|instrument|survey)\b/i;
/**
 * Abbreviations in parentheses that name a safety, coding or reporting standard rather than an instrument, so a
 * convention carrying one ("Adverse event (AE) severity", "Grade (CTCAE)") still routes by topic.
 */
const NOT_INSTRUMENT_ABBREV = new Set(["ae", "aes", "sae", "saes", "ade", "sade", "aesi", "susar", "crf", "ecg", "ekg", "bmi", "ctcae", "meddra", "who", "whodrug", "ons", "ich", "gcp", "us", "uk", "eu", "nci", "cdisc", "pk", "pd", "hiv", "id", "na", "n/a"]);

/** An abbreviation compared as an abbreviation: lower case, letters and digits only ("HAM-D" -> hamd, "UAS7" -> uas7). */
const normAbbrev = (a: string): string => a.toLowerCase().replace(/[^a-z0-9]/g, "");
/** Excluded (NOT_INSTRUMENT_ABBREV) with or without a trailing version number ("CTCAE5" is CTCAE). */
const excludedAbbrev = (a: string): boolean => a.length < 2 || NOT_INSTRUMENT_ABBREV.has(a) || NOT_INSTRUMENT_ABBREV.has(a.replace(/(?<=[a-z])\d+[a-z]?$/, ""));
/**
 * Two abbreviations name the same instrument: they are equal, or one is the other plus a trailing version number
 * (UAS and UAS7, HAM-D and HAMD-17, EQ-5D and EQ-5D-5L). Two DIFFERENT numbers are two instruments: PHQ-9 and
 * PHQ-15, SF-36 and SF-12, GAD-7 and GAD-2 (checker round of backlog 21 (ww): dropping every trailing number
 * made each pair one instrument).
 */
function sameAbbrev(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const [s, l] = a.length < b.length ? [a, b] : [b, a];
  return s.length >= 3 && !/\d$/.test(s) && l.startsWith(s) && /^\d/.test(l.slice(s.length));
}
const hasAbbrev = (set: Iterable<string>, a: string): boolean => [...set].some((b) => sameAbbrev(a, b));
/** At least two capitals, so "(Yes/No)", "(e.g.)" and "(Tanner)" are not abbreviations; "MoCA", "mRS" and "PedsQL" are. */
const looksAbbrev = (a: string): boolean => (a.match(/[A-Z]/g) ?? []).length >= 2;
/** Abbreviations written in parentheses ("Emotion Dysregulation Inventory (EDI)", "Modified Rankin Scale (mRS)"). */
const PAREN_ABBREV = /\(\s*([A-Za-z][A-Za-z0-9-]{1,11})\s*\)/g;
const parenAbbrevs = (s: string): string[] => [...s.matchAll(PAREN_ABBREV)].map((m) => m[1]).filter(looksAbbrev);
/**
 * Abbreviations written as words outside parentheses: in capitals ("UAS7 Diary", "EDI", "HAMD-17"), or mixed case
 * with an inner capital ("MoCA", "mRS"). A capitalised ordinary word has one capital and is not one.
 */
function wordAbbrevs(s: string): string[] {
  const out: string[] = [];
  const bare = s.replace(/\([^)]*\)/g, " ");
  for (const m of bare.matchAll(/\b([A-Z][A-Z0-9-]{1,9})\b/g)) if (looksAbbrev(m[1])) out.push(m[1]);
  for (const m of bare.matchAll(/(?<![A-Za-z0-9-])([A-Za-z][A-Za-z0-9]{1,7})(?![A-Za-z0-9-])/g)) {
    if (looksAbbrev(m[1]) && /^[a-z]|[a-z][A-Z]/.test(m[1])) out.push(m[1]);
  }
  return out;
}
/** Initials of a name's words ("Bowel Urgency Diary" -> bud, "Post-Study System Usability Questionnaire" -> pssuq); short joining words are skipped. */
const initialsOf = (s: string): string =>
  s.split(/[^A-Za-z0-9]+/).filter((w) => w && !/^(?:of|for|and|the|in|on|to|with|a|an)$/i.test(w)).map((w) => w[0].toLowerCase()).join("");
/** A plural compared as its singular ("Medications" and "medication"); "status", "diagnosis" and "class" are not plurals. */
const stem = (w: string): string => (w.length > 4 && /[^su]s$/.test(w) && !/is$/.test(w) ? w.slice(0, -1) : w);

interface FormIdentity {
  /** Abbreviations the form name carries: in parentheses, or written as a word ("UAS7 Diary", "EDI", "MoCA"). */
  abbrevs: Set<string>;
  /** The form name's words without its abbreviations, so an abbreviation that spells a word ("CARE") never matches that word. */
  words: string[];
  /** Distinctive words: not generic instrument, field or subject words, at least 4 letters. */
  distinct: string[];
  initials: string;
  /** The name before its first parenthesis, spelled out ("Cytokine Release Syndrome" in "Cytokine Release Syndrome (CRS) Grading"). */
  spelled: { initials: string; distinct: string[] };
  /** Two instruments on one form, joined by a slash ("Karnofsky/Lansky Performance Status"). */
  combined: boolean;
}
function formIdentity(name: string): FormIdentity {
  const abbrevs = new Set<string>();
  for (const a of [...parenAbbrevs(name), ...wordAbbrevs(name)]) abbrevs.add(normAbbrev(a));
  for (const a of [...abbrevs]) if (excludedAbbrev(a)) abbrevs.delete(a);
  const bare = name.replace(/\([^)]*\)/g, " ");
  const words = tokens(bare).map(stem);
  const head = name.split("(")[0];
  return { abbrevs, words, distinct: distinctWords(tokens(bare)), initials: initialsOf(bare), spelled: { initials: initialsOf(head), distinct: distinctWords(tokens(head)) }, combined: /[A-Za-z]\s*\/\s*[A-Za-z]/.test(bare) };
}
const isGenericWord = (w: string): boolean => GENERIC_INSTRUMENT.has(w) || GENERIC_FIELD_WORD.has(w) || GENERIC_SUBJECT_WORD.has(w);
const distinctWords = (ws: string[]): string[] =>
  ws.filter((w) => w.length >= 4 && !isGenericWord(w) && !isGenericWord(stem(w))).map(stem);

/**
 * The LABEL a convention is about: its stored field; failing that, the short head before its first ":" or "="
 * ("Sex: M=Male"). A sentence with neither ("The scale consists of 66 items ...") has no label, because every
 * word in it is incidental: reading it as a field name is what sent the EDI's scale to the Vineland form.
 */
export function conventionLabel(f: Sourced): string {
  if (f.field) return f.field;
  const m = /^([^:=]+)[:=]/.exec(f.text);
  // A short text with no separator is a title, not a sentence ("Intensity Definition").
  const head = (m ? m[1] : f.text).trim();
  return head.split(/\s+/).length <= (m ? 8 : 5) ? head : "";
}

/**
 * The instrument a convention's label names, or null. Named by an abbreviation in parentheses
 * ("School Situations Questionnaire (SSQ) scores") or by an instrument word ("Bristol Stool Scale",
 * "Mayo score: Stool frequency subscore", "Bowel Urgency Diary response scale"). The name is the words before
 * the abbreviation, or up to and including the FIRST instrument word.
 */
function conventionInstrument(label: string): { abbrevs: string[]; nameWords: string[]; initials: string } | null {
  const abbrevs = parenAbbrevs(label).map(normAbbrev).filter((a) => !excludedAbbrev(a));
  if (abbrevs.length > 0) {
    const first = [...label.matchAll(PAREN_ABBREV)].find((m) => looksAbbrev(m[1]));
    const name = first && first.index !== undefined ? label.slice(0, first.index) : label;
    return { abbrevs, nameWords: distinctWords(tokens(name)), initials: initialsOf(name) };
  }
  // An instrument word inside a parenthetical describes the field ("Skin irritation (erythema/irritation scale)"); it does not name an instrument.
  const outside = label.replace(/\([^)]*\)/g, " ");
  const m = INSTRUMENT_NAME_WORD.exec(outside);
  if (!m) return null;
  const name = outside.slice(0, m.index + m[0].length);
  const nameWords = distinctWords(tokens(name));
  if (nameWords.length === 0) return null; // "Total score", "Response scale": no instrument is named.
  return { abbrevs: [], nameWords, initials: initialsOf(name) };
}

/**
 * The convention names an instrument: true only when this form IS that instrument, by abbreviation, by initials,
 * or by its name's words. A shared abbreviation is not enough when BOTH sides spell their name out to that
 * abbreviation and the names share no distinctive word: "Simulator Sickness Questionnaire (SSQ)" is not "School
 * Situations Questionnaire (SSQ)", and "Motivation Assessment Scale (MAS)" is not "Modified Ashworth Scale".
 */
function sameInstrument(ins: { abbrevs: string[]; nameWords: string[]; initials: string }, form: FormIdentity): boolean {
  const namesDisagree = (a: string) =>
    sameAbbrev(ins.initials, a) && sameAbbrev(form.spelled.initials, a) &&
    ins.nameWords.length > 0 && form.spelled.distinct.length > 0 && !ins.nameWords.some((w) => form.spelled.distinct.includes(w));
  if (ins.abbrevs.some((a) => (hasAbbrev(form.abbrevs, a) || (a.length >= 3 && sameAbbrev(form.initials, a))) && !namesDisagree(a))) return true;
  // Initials stand in for an abbreviation only when the convention gives none of its own: "Patient Health
  // Questionnaire (PHQ-9)" spells "phq", which must not make it the PHQ-15.
  if (ins.abbrevs.length === 0 && ins.initials.length >= 3 && hasAbbrev(form.abbrevs, ins.initials) && !namesDisagree(ins.initials)) return true;
  if (ins.nameWords.length === 0) return false;
  const shared = ins.nameWords.filter((w) => form.words.includes(w));
  // Both name an abbreviation and the two differ ("Child Behavior Checklist (CBCL)", "Aberrant Behavior
  // Checklist (ABC)"; CGI-S and CGI-I): only the instrument's full name, of two or more words, still matches
  // ("Bristol Stool Form Scale (BSFS)" and "Bristol Stool Scale (BSS)").
  if (ins.abbrevs.length > 0 && form.abbrevs.size > 0) return ins.nameWords.length >= 2 && shared.length === ins.nameWords.length;
  if (shared.length === ins.nameWords.length) return true;
  // The form carries a shortened name: every distinctive word of the form is in the instrument's name, its first
  // word among them ("Mayo Score Assessment" for "Mayo Clinic Score").
  if (form.distinct.includes(ins.nameWords[0]) && form.distinct.every((w) => ins.nameWords.includes(w))) return true;
  // A combined form ("Karnofsky/Lansky Performance Status" for "Lansky Play-Performance Scale"): all but one of
  // three or more name words, the first (the name's own word, "Lansky") among them.
  return form.combined && ins.nameWords.length >= 3 && shared.length >= ins.nameWords.length - 1 && shared.includes(ins.nameWords[0]);
}

/**
 * A convention that names no instrument matches a form by its label's words, never by one incidental shared word:
 * every distinctive word of one is in the other, or two distinctive words are shared, or the single shared
 * distinctive word is the SUBJECT of both (the first distinctive word of each: "Skin irritation" and "Skin
 * Tolerability Assessment"; "Pregnancy test result" and "Pregnancy Assessment").
 */
function labelMatchesForm(label: string, form: FormIdentity): boolean {
  const labelWords = distinctWords(tokens(label.replace(/[()]/g, " ")));
  if (labelWords.length === 0 || form.distinct.length === 0) return false;
  const shared = labelWords.filter((w) => form.distinct.includes(w));
  if (shared.length === 0) return false;
  if (form.distinct.every((w) => labelWords.includes(w)) || labelWords.every((w) => form.distinct.includes(w))) return true;
  if (new Set(shared).size >= 2) return true;
  return labelWords[0] === form.distinct[0];
}

/**
 * The conventions relevant to this form. In order:
 *  1. the study-wide yes/no rule goes to every form;
 *  2. a convention naming an instrument by abbreviation goes ONLY to that instrument's form (before the topic
 *     routes, so "Itch severity (UAS)" does not reach the adverse event form through "severity");
 *  3. a convention whose label matches the form's topic (demographics, adverse event, disposition);
 *  4. a convention naming an instrument in words goes only to that instrument's form;
 *  5. any other convention by its label's words (labelMatchesForm).
 * A convention with no label (a colon-less sentence with no stored field) goes nowhere by words: it has no
 * subject to match, and attaching it by whatever words it happens to contain is the defect in backlog 21 (ww).
 * It is not sent to every form either: the block tells the model to use these facts EXACTLY, so an unplaced
 * fact sent everywhere is a wrong fact on most forms. The protocol text the prompt carries still holds it.
 */
function relevantConventions(all: Sourced[], kinds: Set<FormKind>, formName: string): Sourced[] {
  const topics: RegExp[] = [];
  if (kinds.has("demographics")) topics.push(/\bsex\b|gender|ethnic|\brace\b|racial|\bage\b|birth|height|weight|marital|education/i);
  // Causality, relatedness and attribution are routed by AE_RELATEDNESS above the topics, so a relationship
  // that is not an event's to a product ("Relationship to child") is not an adverse event list.
  if (kinds.has("ae")) topics.push(/severity|intensity|grade|outcome|action taken|seriousness|serious/i);
  if (kinds.has("disposition")) topics.push(/disposition|discontinu|withdraw|completion|reason/i);
  const form = formIdentity(formName);
  return all.filter((f) => {
    if (isYesNoConvention(f)) return true;
    const label = conventionLabel(f);
    if (!label) return false;
    const ins = conventionInstrument(label);
    if (ins && ins.abbrevs.length > 0) return sameInstrument(ins, form);
    // How an event relates to a product ("Device relationship", "Relationship to study drug", "Attribution of
    // the AE") is an adverse event judgement: it belongs on the adverse event forms only, never on a device or
    // drug form through the product's name.
    if (AE_RELATEDNESS.test(label)) return kinds.has("ae");
    if (topics.some((t) => t.test(label))) return true;
    return labelNamesForm(label, form);
  });
}

/**
 * How an adverse event relates to a product: causality, relatedness or attribution always; "relationship" only
 * when a product, procedure or the event itself is named with it ("Relationship to study drug", "Device
 * relationship", "Adverse event relationship"). "Relationship to child" (an informant's or caregiver's) is not.
 */
const RELATED_TO = "drug|drugs|device|devices|treatment|product|procedure|intervention|medication|therapy|vaccine|ip|imp|investigational|ae|aes|sae|saes|event|events";
const AE_RELATEDNESS = new RegExp(`\\b(?:relatedness|causality|causal|attribution)\\b|\\brelationship\\b.*\\b(?:${RELATED_TO})\\b|\\b(?:${RELATED_TO})\\s+relationship\\b`, "i");

/** By its label alone (no topic routing): does this convention name this form? Instruments by their identity, anything else by labelMatchesForm. */
function labelNamesForm(label: string, form: FormIdentity): boolean {
  // An abbreviation the label writes as a word ("UAS7 itch score", "SF-36 item response") that the form also
  // carries. Only ever adds a match: the label's own words still decide every other form.
  // The form may spell it out instead ("PHQ-9 item response" and "Patient Health Questionnaire-9").
  if (wordAbbrevs(label).map(normAbbrev).some((a) => !excludedAbbrev(a) && (hasAbbrev(form.abbrevs, a) || (a.length >= 3 && sameAbbrev(form.initials, a))))) return true;
  const ins = conventionInstrument(label);
  if (!ins) return labelMatchesForm(label, form);
  if (sameInstrument(ins, form)) return true;
  // "Mayo score: Endoscopic (central reading) subscore": the part after the colon is one component of the
  // instrument and may be recorded on its own form ("Endoscopy and Central Reading"); it matches by its own words.
  const component = /:\s*(.+)$/.exec(label)?.[1] ?? "";
  return !!component && labelMatchesForm(component, form);
}

/**
 * The ONE rule for "is this convention about this name" (a form name, or a question's text), shared with the
 * Yes/No coding check (crf/protocolFactChecks.ts W11), whose automatic correction rewrites a field's codes on
 * it. The label is the convention's field or short label (conventionLabel), never a whole sentence.
 */
export function conventionNames(label: string, name: string): boolean {
  if (!label.trim() || !name.trim()) return false;
  return labelNamesForm(label, formIdentity(name));
}

/** A numbered SECTION heading ("4. Study Design", "8.2 Demographics"): short, capitalised, no sentence punctuation. */
const SECTION_HEADING = /^\s*\d+(?:\.\d+)*\.?\s+(?:[A-Z][A-Za-z0-9,&/()'-]*\s*){1,7}$/;
const INSTRUMENT_WORD = /\b(?:questionnaire|scale|index|inventory|instrument|diary|score|survey|appendix|annex)\b/i;
const VISIT_LIKE_PREFIX = /^(?:v|visit|wk|week|day|d|w|ae|sae|cycle|c)$/i;
const APPENDIX_HEADING = /^\s*(?:appendix|annex|attachment)\b/i;

/**
 * A PDF text layer lays an item table out as running lines, not one cell per line: an item may share its
 * line with its identifier and its score ("BUD1 In the past day, how often ...? 0-4"), wrap onto a
 * continuation line, and keep its score on a line of its own. This rewrites each such item as the
 * one-cell-per-line shape the finder already reads (identifier, text, score) and records, per rewritten
 * line, what to print back (the joined item on its identifier line, nothing on the others).
 * Lines that are not such an item pass through unchanged, so Word-layout text is untouched.
 */
const ITEM_IDENTIFIER = String.raw`(?:Item|Question|q)\s*\d{1,3}[a-z]?|[A-Za-z]{1,6}\d{1,3}[a-z]?|\d{1,3}[a-z]?|[a-z](?=[.)])`;
const INLINE_ITEM = new RegExp(`^\\s*(${ITEM_IDENTIFIER})([.):]?)\\s+(\\S.*)$`, "i");
const ITEM_ID_ONLY = new RegExp(`^\\s*(?:${ITEM_IDENTIFIER})\\s*[.):]?\\s*$`, "i");
const TRAILING_RANGE = /^(.*\S)\s+(\d+(?:\.\d+)?\s*(?:-|–|to)\s*\d+(?:\.\d+)?)\s*$/;
const LONE_RANGE = /^\s*\d+(?:\.\d+)?\s*(?:-|–|to)\s*\d+(?:\.\d+)?\s*$/;
// A page footer the PDF reader leaves in the running text ("-- 13 of 19 --").
const PAGE_FOOTER = /^\s*--\s*\d+\s+of\s+\d+\s*--\s*$/;
// A line that starts the scoring rule, never the tail of the item above it.
const SCORING_START = /^\s*(?:the\s+)?(?:total|scor)/i;
// A line that shows a numbered list is an instrument's items: a response scale or an item/score column header.
const SCALE_SIGNAL = /(?:response scale|scale for all|\b\d\s*=\s*[A-Za-z]|\(strongly [a-z]+\)|^\s*(?:no\.?|item|#)\s+(?:item|text|statement|question)\b)/i;
// A bibliography entry: a year, a volume:page run, or author initials.
const REFERENCE_LIKE = /\b(?:19|20)\d{2}\b|\d+\s*:\s*\d+(?:\s*[-–]\s*\d+)?|\b[A-Z][a-z]+ [A-Z]{1,3}[,.]/;
// A sentence that talks ABOUT the instrument is not one of its items.
const ABOUT_INSTRUMENT = /\b(?:questionnaire|diary|instrument|scale|protocol|appendix|study|visit|participants?|investigator|database)\b/i;
const ENDS_SENTENCE = /[.?!:;)"”]\s*$/;
/** The next non-footer line when it is a lone score or range, else null. */
function j0(raw: string[], at: number): string | null {
  return at < raw.length && LONE_RANGE.test(raw[at]) ? raw[at] : null;
}
export function normaliseItemLayout(raw: string[]): { lines: string[]; orig: Array<string | null> } {
  const lines: string[] = [];
  const orig: Array<string | null> = [];
  const wordCount = (t: string) => t.trim().split(/\s+/).length;
  const startsItem = (l: string) => {
    const m = INLINE_ITEM.exec(l);
    return !!m && wordCount(m[3]) >= 4;
  };
  let signalAt = -100;
  let chainEnd = -1;
  for (let i = 0; i < raw.length; i++) {
    if (PAGE_FOOTER.test(raw[i])) continue;
    if (SCALE_SIGNAL.test(raw[i])) signalAt = i;
    const m = INLINE_ITEM.exec(raw[i]);
    // A bare number needs a capitalised sentence after it; letters+number ("BUD1") need only words.
    // ("1. text" is the numbered-list shape the finder already reads as it is.)
    // A bare number then a tab and another tab-separated cell is a table row, never rewritten into a numbered item.
    const tabRow = !!m && !m[2] && /^\s*\S+\t/.test(raw[i]) && /\t/.test(m[3]);
    const ok = !!m && !tabRow && wordCount(m[3]) >= 5 && m[3].length <= 220 && /[a-z]/.test(m[3])
      && !SECTION_HEADING.test(raw[i]) && (/[A-Za-z]/.test(m[1]) || (!m[2] && /^[A-Z]/.test(m[3])));
    if (!ok || !m) { lines.push(raw[i]); orig.push(raw[i]); continue; }
    let text = m[3].trim();
    let score = "";
    const tr = TRAILING_RANGE.exec(text);
    if (tr && wordCount(tr[1]) >= 4) { text = tr[1]; score = tr[2]; }
    // A bare number is a weak identifier: it needs a real item shape. Not a reference, not a sentence about the
    // instrument, and a signal that this is an item table (a score cell, a response scale or column header just
    // above, or the item directly above was one).
    if (/^\d+$/.test(m[1])) {
      const scoreBelow = j0(raw, i + 1) !== null;
      const signalled = !!score || scoreBelow || i - signalAt <= 12 || chainEnd === i;
      if (REFERENCE_LIKE.test(text) || ABOUT_INSTRUMENT.test(text) || !signalled) { lines.push(raw[i]); orig.push(raw[i]); continue; }
    }
    let j = i + 1;
    // Wrapped text: a continuation starts in lower case (or follows a comma) and is not an item, score or heading.
    while (!score && j < raw.length && j - i <= 3 && !ENDS_SENTENCE.test(text)) {
      const n = raw[j];
      if (!n.trim() || n.length > 220 || SCORING_START.test(n) || PAGE_FOOTER.test(n) || startsItem(n) || LONE_RANGE.test(n) || APPENDIX_HEADING.test(n) || SECTION_HEADING.test(n)) break;
      if (!/^\s*[a-z]/.test(n) && !/,\s*$/.test(text)) break;
      text = `${text} ${n.trim()}`;
      j++;
    }
    if (!score && j < raw.length && LONE_RANGE.test(raw[j])) { score = raw[j].trim(); j++; }
    const identifier = /^[a-z]$/i.test(m[1]) ? `${m[1]}.` : m[1];
    lines.push(identifier); orig.push(`${identifier} ${text}${score ? ` ${score}` : ""}`);
    lines.push(text); orig.push(null);
    if (score) { lines.push(score); orig.push(null); }
    i = j - 1;
    chainEnd = j;
  }
  return { lines, orig };
}

/**
 * The passage that defines an instrument, or null. Deliberately conservative: the prompt tells the
 * model to use these items EXACTLY, so a wrong passage is worse than none. A passage needs a short
 * heading line holding the form's distinctive words AND an instrument word, then a run of at least
 * three consecutive short item lines (a bare number followed by its text, "1. text", or a table row).
 * Numbered section headings are not items; a table of contents and a schedule of events never qualify.
 * Licensing assessment can lower the minimum for a printed one- or two-item instrument.
 */
export function findInstrumentPassage(protocolText: string, formName: string, minimumItems = 3): string | null {
  // A parenthetical acronym or abbreviation in the FORM name ("Bowel Urgency Diary (BUD)") is optional: the
  // protocol heading may spell the name out and never use the short form. It is read only when nothing else is left.
  const bare = formName.replace(/\([^)]*\)|\[[^\]]*\]/g, " ");
  let words = tokens(bare).filter((t) => !GENERIC_INSTRUMENT.has(t));
  if (words.length === 0) words = tokens(formName).filter((t) => !GENERIC_INSTRUMENT.has(t));
  // A word written in capitals in the form name ("BUD Diary") is an acronym: a heading that spells it out in
  // three or more consecutive words ("Bowel Urgency Diary") matches it too.
  const acronyms = new Set((formName.match(/\b[A-Z]{3,6}\b/g) ?? []).map((a) => a.toLowerCase()));
  const spellsOut = (acro: string, headingTokens: string[]): boolean => {
    for (let i = 0; i + acro.length <= headingTokens.length; i++) {
      if (headingTokens.slice(i, i + acro.length).map((t) => t[0]).join("") === acro) return true;
    }
    return false;
  };
  if (!protocolText || words.length === 0) return null;
  const { lines, orig } = normaliseItemLayout(protocolText.split(/\r?\n/));
  // Letters then a number with no space ("BUD1", "Q12", "AE3b"), "Item 1" / "Question 3", or a number then a letter ("1a").
  const isItemId = (l: string) => ITEM_ID_ONLY.test(l) && !/^\s*\d{1,3}\s*[.):]?\s*$/.test(l);
  const isScoreCell = (l: string) => /^\s*(?:\d+(?:\.\d+)?\s*(?:-|–|to)\s*\d+(?:\.\d+)?|yes\s*\/\s*no|y\s*\/\s*n|text|numeric|date|time)\s*$/i.test(l);
  // prefix and ordinal of an identifier: BUD3 -> bud/3; ITEM 2 -> item/2; 1b -> "1"/2 (the letter counts up).
  const idParts = (l: string): { prefix: string; n: number } => {
    const t = l.trim().replace(/[.):]+$/, "");
    if (/^[a-z]$/i.test(t)) return { prefix: "letter", n: t.toLowerCase().charCodeAt(0) - 96 };
    const m = /^(\d{1,3})([a-z])$/i.exec(t);
    if (m) return { prefix: m[1], n: m[2].toLowerCase().charCodeAt(0) - 96 };
    const k = /^([A-Za-z]+)\s?(\d{1,3})[a-z]?$/.exec(t);
    return k ? { prefix: k[1].toLowerCase(), n: Number(k[2]) } : { prefix: "", n: 0 };
  };
  // Identifiers in a run must share one prefix and count up from 1 without gaps.
  const idsCountUp = (ids: string[]): boolean => {
    const parts = ids.map(idParts);
    return parts.length > 0 && parts.every((p, i) => p.prefix === parts[0].prefix && p.n === i + 1);
  };
  const isRow = (l: string) => /\t/.test(l) || / \| /.test(l);
  const isBare = (l: string) => /^\s*\d{1,3}\s*[.):]?\s*$/.test(l);
  const isNumberedText = (l: string) => {
    const item = INLINE_ITEM.exec(l);
    // Bare numeric PDF rows qualify only through normaliseItemLayout's scale/reference guards.
    return !!item && (!!item[2] || /[A-Za-z]/.test(item[1])) && l.length <= 220 && !SECTION_HEADING.test(l);
  };
  // Returns how many lines the entry at i takes (0 = not an item).
  const entryAt = (i: number): number => {
    const l = lines[i] ?? "";
    if (isBare(l)) { const n = lines[i + 1] ?? ""; return n.trim() && n.length <= 220 && !isBare(n) && !SECTION_HEADING.test(n) ? 2 : 0; }
    if (isNumberedText(l)) return 1;
    // A table the document extractor wrote one cell per line: an item identifier (BUD1, Q1, ITEM 1, 1a), then
    // the item text, then optionally a score cell ("0-4"). The identifier must be a real one, so an
    // ordinary line such as "Visit 1 takes place" is not an item.
    if (isItemId(l)) {
      const text = lines[i + 1] ?? "";
      if (!text.trim() || text.length > 220 || isItemId(text) || isBare(text) || SECTION_HEADING.test(text) || APPENDIX_HEADING.test(text)) return 0;
      // An item is a question or statement, not a label: visit and adverse event tables are short cells.
      if (text.trim().split(/\s+/).length < 5 || VISIT_LIKE_PREFIX.test(idParts(l).prefix)) return 0;
      return isScoreCell(lines[i + 2] ?? "") ? 3 : 2;
    }
    // A schedule-of-events row (cells marked X) is never an item.
    if (isRow(l) && l.length <= 260 && l.trim().length > 3 && !/\|\s*X\s*(?:\||$)/i.test(l)) return 1;
    return 0;
  };
  const sectionBreak = (l: string) => APPENDIX_HEADING.test(l) || SECTION_HEADING.test(l);
  let best: { idx: number; end: number; score: number } | null = null;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.length > 120 || l.trim().length < 4 || isRow(l) || /;/.test(l) || (l.match(/appendix/gi) ?? []).length > 1) continue;
    // A heading is a title, not a sentence about the instrument.
    if (/[.]\s*$/.test(l.trim()) || l.trim().split(/\s+/).length > 10) continue;
    const lt = new Set(tokens(l));
    const ordered = tokens(l);
    if (!words.every((w) => lt.has(w) || (acronyms.has(w) && spellsOut(w, ordered))) || !INSTRUMENT_WORD.test(l)) continue;
    if (lines.slice(Math.max(0, i - 3), i).some((x) => /schedule of (?:events|assessments)|table of contents/i.test(x))) continue;
    // Up to 8 intro lines (instructions, column headers) that are not items and not another section.
    let j = i + 1;
    let intro = 0;
    while (j < lines.length && intro < 8 && entryAt(j) === 0 && !sectionBreak(lines[j])) { j++; intro++; }
    let run = 0;
    let k = j;
    const ids: string[] = [];
    for (let n = entryAt(k); n > 0; n = entryAt(k)) { if (isItemId(lines[k])) ids.push(lines[k]); run++; k += n; }
    if (run < minimumItems) continue;
    if (ids.length > 0 && !idsCountUp(ids)) continue;
    const score = (APPENDIX_HEADING.test(l) ? 100 : 0) + run;
    if (!best || score > best.score) best = { idx: i, end: k, score };
  }
  if (!best) return null;
  const out: string[] = orig.slice(best.idx, best.end).filter((x): x is string => x !== null);
  let len = out.join("\n").length;
  // Keep what follows the items (the scoring rule) until the next section starts.
  for (let j = best.end; j < lines.length && !sectionBreak(lines[j]) && len < PASSAGE_CHARS; j++) {
    const o = orig[j];
    if (o === null) continue;
    out.push(o);
    len += o.length + 1;
  }
  // The item run must remain complete; only the optional scoring tail is budgeted above.
  return out.join("\n").trim();
}

/** The same extracted layout used by detection, with item boundaries retained for verification. */
export function protocolPrintedItemBlocks(passage: string): Array<{ id: string; text: string }> {
  const { orig } = normaliseItemLayout(passage.split(/\r?\n/));
  // DELIBERATE GAP: a table that numbers its rows ("1 | Difficulty falling asleep | 0-4") is NOT transcribed as printed
  // items. A numbered table cannot be told from an adverse event, criteria, score or lab table, and copying a non-question
  // onto a form as a question is the failure to avoid. Such a passage prints no items, so a licensed instrument gets an
  // empty shell and an unstated one a lookup with a licence note. A table row is also never part of an item's text: a
  // row between items must not be merged into the previous block (a pipe or tab row that is not an option-code line).
  const isTableRow = (l: string) => /[|\t]/.test(l) && !/^\s*\d+\s*[=:)]/.test(l);
  const text = orig.filter((x): x is string => x !== null).join("\n");
  const start = new RegExp(`(?:^|\\n)[ \\t]*(${ITEM_IDENTIFIER})[.):]?[ \\t\\n]+(?=[A-Za-z])`, "gi");
  // A bare number then a tab ("1<tab>Difficulty<tab>0-4") is a table row, not a numbered item ("1.<tab>Question" is).
  const starts = [...text.matchAll(start)].filter((m) => !/^\n?[ \t]*[^\s.):]+\t/.test(m[0]));
  return starts.map((m, i) => {
    const itemLines = text.slice(m.index! + m[0].length, starts[i + 1]?.index ?? text.length).split("\n");
    const cut = itemLines.findIndex((l, n) => n > 0 && isTableRow(l));
    return { id: /^[a-z]$/i.test(m[1]) ? `${m[1]}.` : m[1], text: (cut < 0 ? itemLines : itemLines.slice(0, cut)).join("\n") };
  });
}

export function buildProtocolFactBlock(input: FactFeedInput): FactFeedResult {
  const analysis = input.analysis ?? {};
  const name = input.formName || "";
  const kinds = kindsOf(name, input.formCategory ?? "");
  const max = input.maxChars ?? DEFAULT_FACT_BLOCK_CHARS;

  // [priority, group heading, facts]. Lower priority is kept first when the block is cut.
  const groups: Array<{ title: string; facts: Sourced[] }> = [];
  const add = (title: string, facts: Sourced[]) => { if (facts.length) groups.push({ title, facts }); };

  const conventions = relevantConventions(factsOf(analysis, "codingConventions"), kinds, name);
  add("Coded answer lists and conventions", conventions);
  if (kinds.has("demographics")) {
    // A direct identifier (a name, nickname, email, phone) is never a demographic item, whatever the reading
    // filed it under (backlog 21 (ooo); crf/directIdentifiers.ts). Identifier-only facts are left out and the
    // rule is stated; a fact mixing identifiers with real items is fed with a note naming them.
    const feed = demographicsFactsForFeed(factsOf(analysis, "demographicsFields"));
    add("Demographic items and how they are collected", [
      ...feed.facts,
      ...raceEthnicityFromText(analysis, input.protocolText),
      ...(feed.namedIdentifiers ? [{ text: IDENTIFIER_FEED_RULE }] : []),
    ]);
  }
  if (kinds.has("ae")) {
    add("Seriousness criteria (use these, not a general-purpose list)", factsOf(analysis, "seriousnessCriteria"));
    add("Adverse event severity scale", factsOf(analysis, "aeSeverityScale"));
    add("Adverse event relationship scale", factsOf(analysis, "aeCausalityScale"));
    add("Adverse event reporting period", factsOf(analysis, "aeReportingPeriod"));
    add("Serious event reporting timelines", factsOf(analysis, "saeReportingTimeline"));
    add("Follow-up and outcome", factsOf(analysis, "lostToFollowUpRule"));
  }
  if (kinds.has("disposition")) add("Follow-up and loss to follow-up", factsOf(analysis, "lostToFollowUpRule"));
  if (kinds.has("randomization")) add("Stratification factors", factsOf(analysis, "stratificationFactors"));
  if (kinds.has("ae") || kinds.has("medhistory") || kinds.has("conmed")) {
    const dicts = factsOf(analysis, "codingDictionaries").filter((d) => {
      const s = d.section ?? "";
      return kinds.has("ae") || (kinds.has("medhistory") && /history|\bAE\b|adverse|coding/i.test(s)) || (kinds.has("conmed") && /medication|drug|coding/i.test(s));
    });
    add("Coding dictionaries", dicts);
  }

  const lines: string[] = [];
  let used = 0;
  let cut = 0;
  const emit = (s: string): boolean => {
    if (used + s.length + 1 > max) return false;
    lines.push(s);
    used += s.length + 1;
    return true;
  };

  for (const g of groups) {
    const seen = new Set<string>();
    const facts = g.facts.filter((f) => { const k = f.text.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
    let headed = false;
    for (const f of facts) {
      const line = describeFact(f);
      if (!headed) { if (!emit(`${g.title}:`)) { cut += facts.length; break; } headed = true; }
      if (!emit(line)) cut++;
    }
  }

  let passage: string | null = null;
  if (kinds.has("questionnaire") && input.protocolText) passage = findInstrumentPassage(input.protocolText, name);
  let passageBlock = "";
  if (passage) {
    passageBlock = [
      "The protocol defines this instrument. Use these items EXACTLY as written, in this order, with the response choices and codes given here, and take the scoring rule (total or subscores) from this passage. The score field must follow that rule word for word: if the passage says the total is the sum of the items, the total is the SUM, never a mean or an average; use a mean only when the passage says mean. Do not add, drop, merge or reword items:",
      "<<<",
      passage,
      ">>>",
    ].join("\n");
  }

  if (lines.length === 0 && !passageBlock) return { block: "", cut };

  const out: string[] = [FACT_BLOCK_HEADING, ...lines];
  if (cut > 0) out.push(`(${cut} further stored fact(s) not shown to keep this block short; the protocol text below is the authority.)`);
  if (passageBlock) out.push("", passageBlock);
  return { block: out.join("\n"), cut };
}
